import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { Row } from 'reactstrap';
import Page from '../components/Page/Page.jsx';
import PageNotFound from '../components/Page/PageNotFound.jsx';
import LivePreviewPage from '../components/Page/LivePreviewPage.jsx';
import { ROUTE_KIND, buildRouteTable } from './appRoutes.js';

/** Die aus dem CMS geladenen Seiten; `jury/*` wird vom Jury-Bereich selbst bedient. */
const renderDynamicPageRoutes = (pages) =>
    pages
        .filter((page) => !page.slug.startsWith('jury/'))
        .map((page, i) => (
            /* The following component is monitored by Font Awesome dom.watch(). Check children */
            <Route
                key={i}
                exact //Caution: This may redirect to 404 pag if embedded links are opened from the page.
                path={`/${page.slug}`}
                render={(props) => (
                    <Row>
                        <Page
                            pageTitle={page.title}
                            showTitle={page.showTitle}
                            pageBody={page.body}
                            survey={page.survey}
                            hasExclusiveBlock={page.hasExclusiveBlock}
                            {...props}
                        />
                    </Row>
                )}
            />
        ));

/**
 * Die gemeinsame Routentabelle beider Frontends.
 *
 * Produktspezifische Abweichungen kommen ausschliesslich als Daten herein:
 * eigene Routen ueber `extraRoutes`, eigene Container ueber `containers` und alles,
 * was den ganzen Routenbaum umschliessen muss (Provider, Gates), ueber `renderGate`.
 *
 * @param {object} props
 * @param {Array<object>|null} [props.pages] CMS-Seiten; solange `null`, gibt es weder
 *        dynamische Seiten noch die 404-Route (so verhielten sich beide Apps bisher).
 * @param {Record<string, Function|object>} [props.containers] Overrides fuer die Slots
 *        `Project`, `ProjectList` und `SignUp`; fehlt ein Slot, rendert der Kern-Container.
 * @param {Array<{path: string, component: Function|object, exact?: boolean}>} [props.extraRoutes]
 * @param {object} [props.juryCopy] Produkttexte der Jury-Oberflaeche; landet als `copy` an der
 *        `/jury`-Route (`JuryShell`).
 * @param {string} [props.juryVotingModel] Voting-Modell des Produkts (`competition-ranking` oder
 *        `funding-pool`); landet als `votingModel` an der `/jury`-Route und entscheidet dort ueber
 *        die Finale-Route.
 * @param {(routeTree: React.ReactNode) => React.ReactNode} [props.renderGate] Umschliesst den
 *        Routenbaum; Vorgabe ist die Identitaet.
 * @param {(pageState: boolean) => void} [props.pageNotFoundCallback]
 * @param {(pageData: object) => void} [props.onPageDataChange]
 */
const AppRoutes = ({
    pages = null,
    containers,
    extraRoutes = [],
    juryCopy,
    juryVotingModel,
    renderGate = (routeTree) => routeTree,
    pageNotFoundCallback,
    onPageDataChange,
}) => {
    const routeTable = buildRouteTable({ containers, extraRoutes });

    // `flatMap`, damit die dynamischen Seiten als flache Liste im `<Switch>` landen: ein
    // Fragment als Switch-Kind beendet die Suche sofort (v5 setzt fuer Kinder ohne `path`
    // `match = context.match`, und das ist der Wurzel-Match).
    const routes = routeTable.flatMap((entry, index) => {
        const key = `${entry.kind}-${entry.path || entry.slot || index}`;

        switch (entry.kind) {
            case ROUTE_KIND.LIVE_PREVIEW:
                return (
                    <Route
                        key={key}
                        path={entry.path}
                        render={(routeProps) => (
                            <LivePreviewPage
                                {...routeProps}
                                onPageDataChange={onPageDataChange}
                                pageNotFoundCallback={pageNotFoundCallback}
                            />
                        )}
                    />
                );
            case ROUTE_KIND.DYNAMIC_PAGES:
                return pages ? renderDynamicPageRoutes(pages) : [];
            case ROUTE_KIND.COMPONENT:
            case ROUTE_KIND.CONTAINER_SLOT: {
                const RouteComponent = entry.component;
                const productProps = {
                    ...(entry.juryCopy ? { copy: juryCopy } : {}),
                    ...(entry.juryVotingModel ? { votingModel: juryVotingModel } : {}),
                };
                return (
                    <Route
                        key={key}
                        path={entry.path}
                        exact={entry.exact === true}
                        render={
                            entry.injectRouteProps === false
                                ? () => <RouteComponent {...productProps} />
                                : (routeProps) => <RouteComponent {...routeProps} {...productProps} />
                        }
                    />
                );
            }
            case ROUTE_KIND.NOT_FOUND:
                return pages ? (
                    <Route
                        key={key}
                        path={entry.path}
                        status={404}
                        render={() => <PageNotFound pageNotFoundCallback={pageNotFoundCallback} />}
                    />
                ) : (
                    []
                );
            default:
                return [];
        }
    });

    return renderGate(<Switch>{routes}</Switch>);
};

export default AppRoutes;
